
import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Send, RefreshCw } from 'lucide-react';
import { useReferrals, Referral } from '@/hooks/use-referrals';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface ReferralHistorySectionProps {
  onOpenReferralModal: () => void;
}

const ReferralHistorySection: React.FC<ReferralHistorySectionProps> = ({ 
  onOpenReferralModal 
}) => {
  const { referrals, loading, fetchReferrals } = useReferrals();

  useEffect(() => {
    fetchReferrals();
  }, []);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'PPP', { locale: de });
  };

  const getReferralTypeBadge = (type: string) => {
    if (type === 'studio') {
      return <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">Studio</Badge>;
    }
    return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Kunde</Badge>;
  };

  const getReferralStatusBadge = (status: string, convertedAt?: string | null) => {
    if (status === 'converted') {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          Konvertiert {convertedAt ? `am ${formatDate(convertedAt)}` : ''}
        </Badge>
      );
    }
    return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">Ausstehend</Badge>;
  };

  const getConvertedCount = () => {
    return referrals.filter(ref => ref.status === 'converted').length;
  };

  const getPendingCount = () => {
    return referrals.filter(ref => ref.status === 'pending').length;
  };

  const getStudioCount = () => {
    return referrals.filter(ref => ref.type === 'studio').length;
  };

  const getFriendCount = () => {
    return referrals.filter(ref => ref.type === 'customer').length;
  };

  return (
    <Card className="border border-chrona-gray-700 bg-chrona-gray-900 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg text-chrona-text">Meine Einladungen</CardTitle>
          <CardDescription className="text-chrona-gray-400">
            Verfolge den Status deiner Einladungen und Belohnungen.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReferrals}
            disabled={loading}
            className="border-chrona-gray-700"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <Button
            onClick={onOpenReferralModal}
            size="sm"
            className="bg-chrona-accent hover:bg-chrona-accent-light text-white"
          >
            <Send className="h-4 w-4 mr-2" />
            Neue Einladung
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 border border-chrona-gray-800 rounded-lg">
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : referrals.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <Card className="border border-chrona-gray-700 bg-chrona-gray-800">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-chrona-text">{referrals.length}</p>
                  <p className="text-sm text-chrona-gray-400">Gesamt</p>
                </CardContent>
              </Card>
              <Card className="border border-chrona-gray-700 bg-chrona-gray-800">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-chrona-text">{getConvertedCount()}</p>
                  <p className="text-sm text-chrona-gray-400">Konvertiert</p>
                </CardContent>
              </Card>
              <Card className="border border-chrona-gray-700 bg-chrona-gray-800">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-chrona-text">{getFriendCount()}</p>
                  <p className="text-sm text-chrona-gray-400">Freunde</p>
                </CardContent>
              </Card>
              <Card className="border border-chrona-gray-700 bg-chrona-gray-800">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-chrona-text">{getStudioCount()}</p>
                  <p className="text-sm text-chrona-gray-400">Studios</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="rounded-md border border-chrona-gray-700 overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-chrona-gray-400 uppercase bg-chrona-gray-800">
                  <tr>
                    <th scope="col" className="px-6 py-3">E-Mail</th>
                    <th scope="col" className="px-6 py-3">Typ</th>
                    <th scope="col" className="px-6 py-3">Status</th>
                    <th scope="col" className="px-6 py-3">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral: Referral) => (
                    <tr key={referral.id} className="border-b border-chrona-gray-700 bg-chrona-gray-850 hover:bg-chrona-gray-800">
                      <td className="px-6 py-4 font-medium text-chrona-text">
                        {referral.invited_email}
                      </td>
                      <td className="px-6 py-4">
                        {getReferralTypeBadge(referral.type)}
                      </td>
                      <td className="px-6 py-4">
                        {getReferralStatusBadge(referral.status, referral.converted_at)}
                      </td>
                      <td className="px-6 py-4 text-chrona-gray-400">
                        {formatDate(referral.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-10 bg-chrona-gray-850 rounded-lg border border-dashed border-chrona-gray-700">
            <p className="text-chrona-gray-400 mb-4">Du hast noch keine Einladungen versendet.</p>
            <Button 
              onClick={onOpenReferralModal}
              variant="default"
              className="bg-chrona-accent hover:bg-chrona-accent-light text-white"
            >
              <Send className="h-4 w-4 mr-2" />
              Erste Einladung senden
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralHistorySection;
